import React, { useState, useEffect, useCallback } from 'react';
import { Search, X, Info, CheckCircle, ChevronLeft, ChevronRight, Loader2, AlertCircle, RefreshCw, ChevronDown, ChevronUp } from 'lucide-react';
import { indexesAPI, getErrorMessage } from '../../lib/api';
import { toast } from '../../hooks/use-toast';
import { UpgradeOverlay } from '../../components/UpgradeOverlay';
import { useAccess } from '../../hooks/useAccess';

const IndexSearchContent = () => {
  const { liveIca } = useAccess();
  const maxSelections = liveIca.maxIndexes || 0;
  const itemsPerPage = 20;
  
  // State
  const [selectedIndexes, setSelectedIndexes] = useState([]);
  const [indexes, setIndexes] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [pendingSearch, setPendingSearch] = useState('');
  const [selectionTrayExpanded, setSelectionTrayExpanded] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalIndexes, setTotalIndexes] = useState(0);
  const [loading, setLoading] = useState(true);
  const [selectionsLoading, setSelectionsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [actionLoading, setActionLoading] = useState(null); // index id being acted upon

  // Fetch selected indexes on mount
  const fetchSelectedIndexes = useCallback(async () => {
    setSelectionsLoading(true);
    try {
      const response = await indexesAPI.getSelectedIndexes();
      const data = response.data?.data || response.data;
      setSelectedIndexes(data?.selected_indexes || []);
    } catch (err) {
      console.error('Failed to fetch selected indexes:', err);
    } finally {
      setSelectionsLoading(false);
    }
  }, []);

  // Fetch indexes list
  const fetchIndexes = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await indexesAPI.searchIndexes({
        q: searchQuery,
        page: currentPage,
        limit: itemsPerPage,
      });
      
      const responseData = response.data;
      const indexesData = responseData?.data || [];
      const pagination = responseData?.pagination || {};
      
      setIndexes(indexesData);
      setTotalIndexes(pagination?.total || indexesData.length);
    } catch (err) {
      console.error('Failed to fetch indexes:', err);
      setError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [searchQuery, currentPage]);

  // Initial load
  useEffect(() => {
    fetchSelectedIndexes();
  }, [fetchSelectedIndexes]);

  // Fetch indexes when search or page changes
  useEffect(() => {
    fetchIndexes();
  }, [fetchIndexes]);

  // Handle search
  const handleSearch = () => {
    setSearchQuery(pendingSearch);
    setCurrentPage(1);
  };

  // Handle search on Enter key
  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  // Select an index
  const handleSelectIndex = async (index) => {
    if (selectedIndexes.length >= maxSelections) {
      toast.warning({
        title: 'Selection Limit Reached',
        description: `You can only select up to ${maxSelections} indexes.`,
      });
      return;
    }
    if (selectedIndexes.find(i => i.id === index.id)) return;
    
    setActionLoading(index.id);
    try {
      await indexesAPI.updateSelection({
        index_ids: [index.id],
        action: 'select',
      });
      
      // Add to local state
      setSelectedIndexes(prev => [...prev, {
        id: index.id,
        name: index.name,
        stock_count: index.stock_count,
      }]);
      
      // Update the indexes list to reflect selection
      setIndexes(prev => prev.map(i => 
        i.id === index.id ? { ...i, selected: true } : i
      ));
      
      toast.success({
        title: 'Index Selected',
        description: `${index.name} has been added to your selection.`,
      });
    } catch (err) {
      console.error('Failed to select index:', err);
      toast.error({
        title: 'Selection Failed',
        description: getErrorMessage(err) || 'Failed to select index. Please try again.',
      });
    } finally {
      setActionLoading(null);
    }
  };

  // Deselect an index
  const handleDeselectIndex = async (indexId) => {
    const index = selectedIndexes.find(i => i.id === indexId);
    setActionLoading(indexId);
    try {
      await indexesAPI.updateSelection({
        index_ids: [indexId],
        action: 'deselect',
      });
      
      // Remove from local state
      setSelectedIndexes(prev => prev.filter(i => i.id !== indexId));
      
      // Update the indexes list to reflect deselection
      setIndexes(prev => prev.map(i => 
        i.id === indexId ? { ...i, selected: false } : i
      ));
      
      toast.info({
        title: 'Index Removed',
        description: index ? `${index.name} has been removed from your selection.` : 'Index has been removed from your selection.',
      });
    } catch (err) {
      console.error('Failed to deselect index:', err);
      toast.error({
        title: 'Removal Failed',
        description: getErrorMessage(err) || 'Failed to remove index. Please try again.',
      });
    } finally {
      setActionLoading(null);
    }
  };

  // Toggle index selection
  const handleToggleIndex = (index) => {
    const isSelected = selectedIndexes.find(i => i.id === index.id);
    if (isSelected) {
      handleDeselectIndex(index.id);
    } else {
      handleSelectIndex(index);
    }
  };

  const totalPages = Math.ceil(totalIndexes / itemsPerPage);
  const remainingSelections = maxSelections - selectedIndexes.length;

  // Loading state
  if (loading && selectionsLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <Loader2 className="animate-spin mx-auto mb-3 text-blue-600" size={48} />
          <p className="text-gray-600">Loading indexes...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error && indexes.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <AlertCircle className="mx-auto mb-3 text-red-500" size={48} />
          <p className="text-red-600 font-semibold mb-2">Failed to load indexes</p>
          <p className="text-gray-500 text-sm mb-4">{error}</p>
          <button
            onClick={fetchIndexes}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2 mx-auto"
          >
            <RefreshCw size={18} />
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Selected Indexes Display */}
      {selectedIndexes.length > 0 && (
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
              <CheckCircle className="text-green-600" size={24} />
              My Watchlist ({selectedIndexes.length})
            </h3>
            <button
              onClick={() => setSelectionTrayExpanded(!selectionTrayExpanded)}
              className="flex items-center gap-1 text-blue-600 hover:text-blue-700 font-medium text-sm"
            >
              {selectionTrayExpanded ? (
                <>
                  <ChevronUp size={18} />
                  Collapse
                </>
              ) : (
                <>
                  <ChevronDown size={18} />
                  Expand
                </>
              )}
            </button>
          </div>
          {selectionTrayExpanded && (
            <div className="flex flex-wrap gap-2">
              {selectedIndexes.map(index => (
                <div 
                  key={index.id} 
                  className="flex items-center gap-2 bg-blue-100 text-blue-800 px-4 py-2 rounded-lg font-semibold"
                >
                  <span>{index.name}</span>
                  <button
                    onClick={() => handleDeselectIndex(index.id)}
                    disabled={actionLoading === index.id}
                    className="hover:bg-blue-200 rounded-full p-1 transition-colors disabled:opacity-50"
                  >
                    {actionLoading === index.id ? (
                      <Loader2 size={16} className="animate-spin" />
                    ) : (
                      <X size={16} />
                    )}
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Progress Bar */}
      <div className="bg-blue-600 text-white rounded-lg p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="bg-white text-blue-600 rounded-full w-12 h-12 flex items-center justify-center font-bold text-lg">
              {selectedIndexes.length}/{maxSelections}
            </div>
            <span className="font-semibold">Indexes Selected</span>
          </div>
          <div className="flex items-center gap-3">
            <div className="bg-white text-blue-600 rounded-full w-12 h-12 flex items-center justify-center font-bold text-lg">
              {remainingSelections}
            </div>
            <span className="font-semibold">Remaining Selections</span>
          </div>
        </div>
      </div>

      {/* Search Section */}
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-2">
          <Search className="text-blue-600" size={28} />
          Index Search
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-4 mb-6">
          <div className="md:col-span-8">
            <label className="block text-sm font-semibold text-gray-700 mb-2">Search Query:</label>
            <input
              type="text"
              value={pendingSearch}
              onChange={(e) => setPendingSearch(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Enter index name..."
              className="w-full border-2 border-gray-300 rounded-lg px-4 py-3 focus:outline-none focus:border-blue-500 transition-colors"
            />
          </div>

          <div className="md:col-span-2 flex items-end">
            <button
              onClick={handleSearch}
              disabled={loading}
              className="w-full bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors font-semibold flex items-center justify-center gap-2 disabled:opacity-50"
            >
              {loading ? (
                <Loader2 size={20} className="animate-spin" />
              ) : (
                <Search size={20} />
              )}
              Search
            </button>
          </div>

          <div className="md:col-span-2 flex items-end">
            <button
              onClick={() => {
                setPendingSearch('');
                setSearchQuery('');
                setCurrentPage(1);
              }}
              disabled={loading || (!pendingSearch && !searchQuery)}
              className="w-full border-2 border-gray-300 text-gray-700 px-6 py-3 rounded-lg hover:bg-gray-50 transition-colors font-semibold flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <X size={20} />
              Clear
            </button>
          </div>
        </div>

        {/* How to Search Info */}
        <div className="bg-blue-50 border-l-4 border-blue-500 rounded-lg p-4">
          <h3 className="font-bold text-blue-900 mb-2 flex items-center gap-2">
            <Info size={18} />
            How to Search
          </h3>
          <ul className="text-sm text-blue-800 space-y-1">
            <li>• Use the search field to find indexes by Index Name</li>
            <li>• Search is case-insensitive and supports partial matches</li>
            <li>• Click "Add to Watchlist" to track an index for LIVE announcements</li>
            <li>• You can add up to {maxSelections} indexes to your watchlist</li>
          </ul>
        </div>
      </div>

      {/* Available Indexes Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="bg-green-50 border-l-4 border-green-500 p-4 m-6 rounded-lg">
          <p className="text-sm text-green-800 flex items-center gap-2">
            <Info size={16} />
            Showing {indexes.length} of {totalIndexes} indexes. Page {currentPage} of {totalPages || 1}.
          </p>
        </div>

        <div className="px-6 pb-2">
          <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
            ☰ All Available Indexes
            <span className="text-sm bg-gray-200 text-gray-700 px-3 py-1 rounded-full">{totalIndexes} Total</span>
          </h3>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="animate-spin text-blue-600" size={40} />
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b-2 border-gray-200">
                  <tr>
                    <th className="px-6 py-4 text-left text-sm font-bold text-gray-700 uppercase">S.NO.</th>
                    <th className="px-6 py-4 text-left text-sm font-bold text-gray-700 uppercase">INDEX NAME</th>
                    <th className="px-6 py-4 text-left text-sm font-bold text-gray-700 uppercase">TOTAL STOCKS</th>
                    <th className="px-6 py-4 text-left text-sm font-bold text-gray-700 uppercase">ACTION</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {indexes.map((index, idx) => {
                    const isSelected = selectedIndexes.find(i => i.id === index.id) || index.selected;
                    const isDisabled = !isSelected && selectedIndexes.length >= maxSelections;
                    const isLoading = actionLoading === index.id;
                    
                    return (
                      <tr 
                        key={index.id} 
                        className={`hover:bg-gray-50 ${isDisabled ? 'opacity-50' : ''}`}
                      >
                        <td className="px-6 py-4 text-gray-900">
                          {(currentPage - 1) * itemsPerPage + idx + 1}
                        </td>
                        <td className="px-6 py-4">
                          <span className="text-gray-900 font-medium">{index.name}</span>
                        </td>
                        <td className="px-6 py-4">
                          <span className="inline-block bg-gray-200 text-gray-700 px-3 py-1 rounded-full text-sm font-semibold">
                            {index.stock_count} stocks
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          {isLoading ? (
                            <Loader2 size={20} className="animate-spin text-blue-600" />
                          ) : isSelected ? (
                            <button
                              onClick={() => handleDeselectIndex(index.id)}
                              className="px-3 py-1.5 text-sm font-medium text-red-600 bg-red-50 border border-red-200 rounded-lg hover:bg-red-100 transition-colors whitespace-nowrap"
                            >
                              Remove from Watchlist
                            </button>
                          ) : (
                            <button
                              onClick={() => handleSelectIndex(index)}
                              disabled={isDisabled}
                              className="px-3 py-1.5 text-sm font-medium text-green-600 bg-green-50 border border-green-200 rounded-lg hover:bg-green-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                            >
                              Add to Watchlist
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {indexes.length === 0 && (
              <div className="text-center py-12">
                <Search className="mx-auto text-gray-300 mb-4" size={64} />
                <p className="text-gray-500 text-lg">No indexes found</p>
                <p className="text-gray-400 text-sm">Try adjusting your search query</p>
              </div>
            )}

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="border-t p-4 flex items-center justify-between">
                <div className="text-sm text-gray-600">
                  Showing {(currentPage - 1) * itemsPerPage + 1} to {Math.min(currentPage * itemsPerPage, totalIndexes)} of {totalIndexes} indexes
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                    disabled={currentPage === 1 || loading}
                    className="px-4 py-2 border rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                  >
                    <ChevronLeft size={16} />
                    Previous
                  </button>
                  <div className="flex gap-1">
                    {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                      let pageNum;
                      if (totalPages <= 5) {
                        pageNum = i + 1;
                      } else if (currentPage <= 3) {
                        pageNum = i + 1;
                      } else if (currentPage >= totalPages - 2) {
                        pageNum = totalPages - 4 + i;
                      } else {
                        pageNum = currentPage - 2 + i;
                      }
                      return (
                        <button
                          key={pageNum}
                          onClick={() => setCurrentPage(pageNum)}
                          disabled={loading}
                          className={`px-4 py-2 rounded-lg ${
                            currentPage === pageNum
                              ? 'bg-blue-600 text-white'
                              : 'border hover:bg-gray-50'
                          }`}
                        >
                          {pageNum}
                        </button>
                      );
                    })}
                  </div>
                  <button
                    onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                    disabled={currentPage === totalPages || loading}
                    className="px-4 py-2 border rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                  >
                    Next
                    <ChevronRight size={16} />
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Selection Info */}
      {selectedIndexes.length >= maxSelections && (
        <div className="bg-yellow-50 border-l-4 border-yellow-500 rounded-lg p-4">
          <p className="text-sm text-yellow-800 font-semibold flex items-center gap-2">
            <Info size={16} />
            You have reached the maximum selection limit ({maxSelections} indexes). Remove an index to select a different one.
          </p>
        </div>
      )}
    </div>
  );
};

// Wrapper component with access control
const IndexSearch = () => {
  return (
    <UpgradeOverlay service="index_selection">
      <IndexSearchContent />
    </UpgradeOverlay>
  );
};

export default IndexSearch;
